/**
 * Estado do "Próximos dias" (Zustand). Registra os codecs de URL `y` (a
 * variável e a opacidade; ligado = no modo) e `d` (o instante previsto) no
 * import — `ui/modules.tsx` importa este arquivo antes de `readUrlState()`.
 *
 * O MODO e a camada andam juntos (decisão do julgamento, `docs/proposta-previsao.md`):
 * ligar entra no modo — a barra de tempo vira a barra da previsão e as
 * camadas observadas de evento somem do mapa (cada uma consulta
 * `emModoPrevisao()` ao decidir a própria visibilidade, sem gravar nada na
 * store dela); desligar ("◂ Observado") volta tudo como estava.
 *
 * Nem `y` nem `d` vão para as preferências salvas: a visita seguinte abre no
 * observado, como sempre.
 */
import { create } from 'zustand';
import { registerUrlCodec } from '../state/urlCodec';
import { MODELOS_TEMPO, modeloTempoEmUso, type IndicePrevisao, type ModeloId, type ModeloTempo } from './api';
import type { CenarioComo, FumacaComo, VariavelId } from './camadas';
import type { Lugar } from './carregarPonto';
import { VARIAVEIS } from './camadas';

export const DEFAULT_VARIAVEL: VariavelId = 'chuva';
export const DEFAULT_OPACIDADE = 0.75;
export const LIMITES_OPACIDADE = { min: 0.2, max: 1, step: 0.05 } as const;

export type EstadoCarga = 'idle' | 'loading' | 'ready' | 'error';

/** O passo na tela: `ok` desenhado · `carregando` (fica o anterior) · `sem` fora da rodada · `falhou`. */
export type EstadoNoPasso = 'ok' | 'carregando' | 'sem' | 'falhou';

export interface PrevisaoState {
  /** No modo "Próximos dias" (= camada ligada). */
  modo: boolean;
  variavel: VariavelId;
  fumaca: FumacaComo;
  /** Cenários do WeatherNext 2: chance de chuva ou de chuva forte. */
  cenario: CenarioComo;
  opacidade: number;
  /** Instante previsto na tela (epoch ms), ou null antes do índice. */
  instante: number | null;
  /** Rodadas fixadas ao entrar no modo (mapa e ficha usam a mesma). */
  rodadas: Partial<Record<ModeloId, string>>;
  /** Chegou rodada mais nova que a fixada (o aviso oferece trocar). */
  rodadaNova: boolean;
  indice: IndicePrevisao | null;
  situacao: { estado: EstadoCarga; erro: string | null };
  noPasso: EstadoNoPasso;
  tocando: boolean;
  /**
   * Exportando a previsão: trava a barra, as teclas, a legenda, o painel e a
   * entrada/saída do modo. É uma trava PRÓPRIA — a do loop (`setExportLock`)
   * faria o app baixar as 48 h do GOES e mudaria o modo das camadas dele, só
   * para um fundo parado.
   */
  exportando: boolean;
  /**
   * O ponto da ficha (lon, lat): a fonte da verdade — o cartão (desktop) e a
   * folha (celular) mostram a ficha dele, o mapa contorna a área e o link o
   * leva (`l`). Só existe com o painel na vista do ponto.
   */
  ponto: [number, number] | null;
  /**
   * O painel da previsão (cartão à direita no desktop, folha no celular):
   * fechado, os ajustes ou a ficha do ponto. Não vai para o link nem para as
   * preferências; sair do modo fecha.
   */
  painel: 'ajustes' | 'ponto' | null;
  /**
   * A altura da folha do celular (em pé): a média deixa o mapa e o ponto à
   * vista; a alta, a ficha inteira. Fechar volta à média.
   */
  encaixe: 'media' | 'alta';
  /** O lugar do ponto da ficha (município e fuso), para o cabeçalho do painel; null enquanto não sabe. */
  lugarDoPonto: Lugar | null;
  /** Ponto pedido pelo link (`l`), ainda não aberto: a ficha abre quando o modo e o índice estiverem prontos. */
  pontoDoLink: [number, number] | null;
  /** Legenda do mapa recolhida; null = automático (recolhida no celular deitado, onde cobriria metade do mapa). */
  legendaRecolhida: boolean | null;
  /**
   * No modo, os botões das camadas observadas (trilho do desktop e do celular)
   * à mostra, abaixo dos da previsão. Entrar no modo os encolhe.
   */
  observadoAberto: boolean;
  /**
   * Desktop: o painel lateral da previsão (à esquerda, como o das camadas
   * observadas), com a opacidade e os ajustes da variável no mapa. Abre pela
   * seta do botão da previsão no trilho; entrar no modo não o abre (pedido do
   * usuário, 21/10/2026). Sair do modo fecha.
   */
  lateral: boolean;
  /**
   * Acesso de pesquisa confirmado (o WeatherNext 2 está no índice): os
   * cenários aparecem no trilho, na ficha e nos ajustes. Ver `acesso.ts`.
   */
  pesquisa: boolean;
  /** O pedido de usuário e senha do acesso de pesquisa aberto (o link `?pesquisa`). */
  pedirAcesso: boolean;
  /**
   * O modelo do tempo escolhido (IFS ou AIFS). Sem o AIFS no índice vale o IFS
   * (`modeloTempoEmUso`). Vai no link (`o=aifs`) e nas preferências.
   */
  tempo: ModeloTempo;

  setModo: (on: boolean) => void;
  setVariavel: (v: VariavelId) => void;
  setFumaca: (f: FumacaComo) => void;
  setCenario: (c: CenarioComo) => void;
  setOpacidade: (o: number) => void;
  setInstante: (t: number | null) => void;
  setTocando: (on: boolean) => void;
  /** Abre a ficha do ponto no painel (o toque no mapa, o link, "Próximos dias aqui"). */
  abrirPonto: (lon: number, lat: number) => void;
  abrirAjustes: () => void;
  fecharPainel: () => void;
  setEncaixe: (e: 'media' | 'alta') => void;
  setObservadoAberto: (on: boolean) => void;
  setLateral: (on: boolean) => void;
}

export const usePrevisaoStore = create<PrevisaoState>()((set) => ({
  modo: false,
  variavel: DEFAULT_VARIAVEL,
  fumaca: 'pm25',
  cenario: 'chance',
  opacidade: DEFAULT_OPACIDADE,
  instante: null,
  rodadas: {},
  rodadaNova: false,
  indice: null,
  situacao: { estado: 'idle', erro: null },
  noPasso: 'sem',
  tocando: false,
  exportando: false,
  legendaRecolhida: null,
  observadoAberto: false,
  lateral: false,
  pesquisa: false,
  pedirAcesso: false,
  tempo: 'ifs',
  ponto: null,
  painel: null,
  encaixe: 'media',
  lugarDoPonto: null,
  pontoDoLink: null,

  setModo: (modo) => set(modo ? { modo, observadoAberto: false } : { modo, tocando: false, ponto: null, painel: null, encaixe: 'media', lugarDoPonto: null, lateral: false }),
  setVariavel: (variavel) => set({ variavel }),
  setFumaca: (fumaca) => set({ fumaca }),
  setCenario: (cenario) => set({ cenario }),
  setOpacidade: (opacidade) => set({ opacidade }),
  setInstante: (instante) => set({ instante }),
  setTocando: (tocando) => set({ tocando }),
  abrirPonto: (lon, lat) => set((s) => (s.ponto?.[0] === lon && s.ponto[1] === lat ? { painel: 'ponto' } : { ponto: [lon, lat], painel: 'ponto', lugarDoPonto: null })),
  abrirAjustes: () => set({ ponto: null, painel: 'ajustes', lugarDoPonto: null }),
  fecharPainel: () => set({ ponto: null, painel: null, encaixe: 'media', lugarDoPonto: null }),
  setEncaixe: (encaixe) => set({ encaixe }),
  setObservadoAberto: (observadoAberto) => set({ observadoAberto }),
  setLateral: (lateral) => set({ lateral }),
}));

/**
 * `<html data-modo-previsao>` no modo: o CSS do trilho do celular (index.css)
 * mostra os botões da previsão mesmo com o trilho recolhido fora do modo.
 */
function marcarNoDocumento(modo: boolean): void {
  if (typeof document === 'undefined') return;
  if (modo) document.documentElement.dataset.modoPrevisao = '';
  else delete document.documentElement.dataset.modoPrevisao;
}
usePrevisaoStore.subscribe((a, b) => {
  if (a.modo !== b.modo) marcarNoDocumento(a.modo);
});

/** Para as camadas observadas: estamos no modo "Próximos dias"? */
export function emModoPrevisao(): boolean {
  return usePrevisaoStore.getState().modo;
}

/** Avisa quando o modo muda (as camadas observadas reavaliam a visibilidade). */
export function aoMudarModo(fn: (modo: boolean) => void): () => void {
  return usePrevisaoStore.subscribe((a, b) => {
    if (a.modo !== b.modo) fn(a.modo);
  });
}

// ---------------------------------------------------------------------------
// Link: `y=chuva`, `y=fumaca-aod`, `y=cenarios-forte`, com `o0.6` quando a
// opacidade difere do padrão (ex.: `y=temperaturao0.6`); `d=2026101915`
// (AAAAMMDDHH, UTC). Sem o acesso de pesquisa, `y=cenarios` vira chuva quando
// o índice chega (`motor.ts`).

type CamposDoLink = 'modo' | 'variavel' | 'fumaca' | 'cenario' | 'opacidade';

export function encodeVariavel(s: Pick<PrevisaoState, CamposDoLink>): string | null {
  if (!s.modo) return null;
  const v = s.variavel === 'fumaca' && s.fumaca === 'aod' ? 'fumaca-aod' : s.variavel === 'cenarios' && s.cenario === 'forte' ? 'cenarios-forte' : s.variavel;
  return s.opacidade !== DEFAULT_OPACIDADE ? `${v}o${s.opacidade}` : v;
}

export function decodeVariavel(value: string): Partial<Pick<PrevisaoState, CamposDoLink>> | null {
  const m = /^([a-z]+)(-aod|-forte)?(?:o(\d*\.?\d+))?$/.exec(value);
  if (!m || !(VARIAVEIS as readonly string[]).includes(m[1])) return null;
  const out: Partial<Pick<PrevisaoState, CamposDoLink>> = {
    modo: true,
    variavel: m[1] as VariavelId,
    fumaca: m[2] === '-aod' && m[1] === 'fumaca' ? 'aod' : 'pm25',
    cenario: m[2] === '-forte' && m[1] === 'cenarios' ? 'forte' : 'chance',
  };
  if (m[3]) {
    const n = Number(m[3]);
    if (Number.isFinite(n)) out.opacidade = Math.min(LIMITES_OPACIDADE.max, Math.max(LIMITES_OPACIDADE.min, n));
  }
  return out;
}

export function encodeInstante(t: number | null): string | null {
  if (t === null) return null;
  const d = new Date(t);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}`;
}

export function decodeInstante(value: string): number | null {
  const m = /^(\d{4})(\d{2})(\d{2})(\d{2})$/.exec(value);
  if (!m) return null;
  const t = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4]);
  return Number.isFinite(t) && +m[2] >= 1 && +m[2] <= 12 && +m[4] < 24 ? t : null;
}

registerUrlCodec({
  key: 'y',
  encode: () => encodeVariavel(usePrevisaoStore.getState()),
  persist: () => null,
  decode: (value) => {
    const s = decodeVariavel(value);
    if (s) usePrevisaoStore.setState(s);
  },
  subscribe: (onChange) =>
    usePrevisaoStore.subscribe((a, b) => {
      if (a.modo !== b.modo || a.variavel !== b.variavel || a.fumaca !== b.fumaca || a.cenario !== b.cenario || a.opacidade !== b.opacidade) onChange();
    }),
});

/** O modelo do tempo em uso agora (o escolhido, se o índice o tem; senão o IFS). */
export function tempoEmUso(s: Pick<PrevisaoState, 'tempo' | 'indice'>): ModeloTempo {
  return modeloTempoEmUso(s.tempo, s.indice);
}

/**
 * `o=aifs` (o modelo do tempo; o IFS, padrão, não vai). Vai também para as
 * preferências: quem prefere o AIFS o encontra na próxima visita.
 */
registerUrlCodec({
  key: 'o',
  encode: () => {
    const t = usePrevisaoStore.getState().tempo;
    return t === 'ifs' ? null : t;
  },
  decode: (value) => {
    if ((MODELOS_TEMPO as readonly string[]).includes(value)) usePrevisaoStore.setState({ tempo: value as ModeloTempo });
  },
  subscribe: (onChange) =>
    usePrevisaoStore.subscribe((a, b) => {
      if (a.tempo !== b.tempo) onChange();
    }),
});

/** `?pesquisa`: abre o pedido de usuário e senha do acesso de pesquisa (e sai do link). */
registerUrlCodec({
  key: 'pesquisa',
  encode: () => null,
  persist: () => null,
  decode: () => usePrevisaoStore.setState({ pedirAcesso: true }),
  subscribe: () => () => {},
});

/** `l=-3.101,-60.025` (lat, lon com 3 casas: ~100 m, menos que a célula). */
export function encodePonto(p: [number, number] | null): string | null {
  return p ? `${p[1].toFixed(3)},${p[0].toFixed(3)}` : null;
}

export function decodePonto(value: string): [number, number] | null {
  const m = /^(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)$/.exec(value);
  if (!m) return null;
  const lat = Number(m[1]);
  const lon = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lon) <= 180 ? [lon, lat] : null;
}

registerUrlCodec({
  key: 'l',
  encode: () => {
    const s = usePrevisaoStore.getState();
    return s.modo ? encodePonto(s.ponto) : null;
  },
  persist: () => null,
  decode: (value) => {
    // A ficha abre quando o modo e o índice estiverem prontos (`usePrevisaoLayers`).
    const p = decodePonto(value);
    if (p) usePrevisaoStore.setState({ pontoDoLink: p });
  },
  subscribe: (onChange) =>
    usePrevisaoStore.subscribe((a, b) => {
      if (a.ponto !== b.ponto || a.modo !== b.modo) onChange();
    }),
});

registerUrlCodec({
  key: 'd',
  encode: () => {
    const s = usePrevisaoStore.getState();
    return s.modo ? encodeInstante(s.instante) : null;
  },
  persist: () => null,
  decode: (value) => {
    // Aplicado quando o índice chegar: o passo mais próximo disponível (ver `motor.ts`).
    const t = decodeInstante(value);
    if (t !== null) usePrevisaoStore.setState({ instante: t, modo: true });
  },
  subscribe: (onChange) =>
    usePrevisaoStore.subscribe((a, b) => {
      if (a.instante !== b.instante || a.modo !== b.modo) onChange();
    }),
});
